import React, { useEffect, useState } from 'react'
import { Typography, Box, Button, Tooltip, Paper, useTheme, IconButton, CircularProgress } from '@mui/material'
import { Link, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { downloadParticipationReport } from '../../../utils/exam-utilities'
import parse from 'html-react-parser'
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled'
import api from '../../../service/api'
import { EditableMathField } from 'react-mathquill'
import DataLoadingSpinner from '../../../components/common/DataLoadingSpinner'
import BreadcrumbList from '../../../components/common/BreadcrumbList'
import { useUserStore } from '../../../store/user.store'
import { observer } from 'mobx-react'
import { Icons, formatDate } from '../../../utils/utilities'
import { UploadOutlined } from '@mui/icons-material'
import CountCard from '../../../components/common/CountCard'
import { useExamStore } from '../../../store/exam.store'
import noDataImg from '../../../assets/no-data.svg'

const ParticipationDetails = observer( () => {
    const { participation_id, exam_id } = useParams()
    const [answers, setAnswers] = useState( null )
    const [questions, setQuestions] = useState( null )
    const [participation, setParticipation] = useState( null )
    const [inProgress, setInProgress] = useState( true )
    const [showRemarks, setShowRemarks] = useState( true )
    const [isScoreHidden, setIsScoreHidden] = useState( false )

    const UserStore = useUserStore()
    const ExamStore = useExamStore()

    const { palette, border, layout } = useTheme()

    const getAnswerText = ( id, options ) => {
        for ( let i = 0; i < options.length; i++ ) {
            if ( id === options[i].answer_id )
                return options[i].answer_text.includes( "LATEX-" ) ? <EditableMathField id="static-latex" style={{ color: "inherit !important", padding: "0" }} latex={options[i].answer_text.split( "LATEX-" )[1]} /> : parse( options[i].answer_text )
        }
    }


    useEffect( () => {
        const getData = async () => {
            try {
                await ExamStore.fetchExam( exam_id )
                if ( !ExamStore.getExam?.exam?.exam_constraints?.hide_scores ) {
                    const { data: participationDataRes } = await api.fetchUserParticipation( participation_id )
                    const { data: submissionDataRes } = await api.fetchParticipationSubmissions( exam_id, participation_id )
                    const { data: questionsDataRes } = await api.fetchQuestions( exam_id )
                    const participationData = participationDataRes.data || participationDataRes
                    const submissionData = submissionDataRes.data || submissionDataRes
                    const questionsData = questionsDataRes.data || questionsDataRes

                    let subs = {}
                    submissionData.forEach( sub => {
                        subs[sub.question_id] ? subs[sub.question_id].push( sub ) : subs[sub.question_id] = [sub]
                    } )
                    setAnswers( subs )
                    setQuestions( questionsData )
                    setParticipation( participationData )
                } else {
                    setIsScoreHidden( true )
                }
            } catch ( e ) {
                console.log( e )
                if ( e?.response?.status === 401 || e?.response?.data?.unauth ) {
                    UserStore.setERPSessionState( "ended" )
                }
                else
                    toast( e?.response?.data?.message || "Something went wrong! try again later" )
            } finally {
                setInProgress( false )
            }
        }

        getData()
    }, [participation_id, exam_id, UserStore] )


    return (
        <Box minHeight={{ md: "500px", xs: "900px" }} sx={{ padding: "20px" }} flexGrow={1} alignItems="stretch" display="flex" flexDirection="column" overflow="auto">

            <BreadcrumbList items={[
                { link: "/student/exams", label: "Exams" },
                { label: inProgress ? <CircularProgress size={14} /> : ExamStore.getExam?.exam?.exam_name, link: `/student/exams/${exam_id}` },
                { link: "/student/scores", label: "Scores" },
            ]} />

            {!isScoreHidden && participation && !inProgress && <Box flexGrow={{ sm: "1", xs: "initial" }} display="flex" flexDirection={{ lg: "row-reverse", md: "row-reverse", sm: "column", xs: "column" }} gap="20px">
                <Box sx={{ flexGrow: "1", position: "relative", display: "flex", flexDirection: "column", borderRadius: "5px" }} >
                    <Box
                        gap="20px"
                        display="flex"
                        flexDirection={{ md: "row", xs: "column" }}
                        justifyContent="space-between"
                        alignItems={{ md: "center", xs: "flex-start" }}
                    >
                        <Box sx={{ zIndex: 10, position: "relative" }}>
                            <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">  {participation && participation.participation.exam_title}</Typography>
                            <Typography variant="subtitle2" fontSize="12px" color='textSecondary'> {`${formatDate( participation.participation.exam_participation_start_time )}, ${new Date( participation.participation.exam_participation_start_time ).toLocaleTimeString()}`}</Typography>
                        </Box>
                        <Button
                            color='customThemeColor'
                            onClick={() => downloadParticipationReport( participation.participation, questions, answers )}
                            sx={{
                                textTransform: "capitalize",
                                minWidth: "max-content",
                                background: `linear-gradient(200.17deg, #F3C400 -47.65%, #994322 167.67%)`,
                                '&:hover': {
                                    background: `linear-gradient(200.17deg, #F3C400 -47.65%, #994322 167.67%)`
                                }
                            }} endIcon={<UploadOutlined />} variant='contained' disableElevation >
                            Export Report
                        </Button>
                    </Box>
                    <Box marginTop="20px" display="grid" gap="20px" width="100%" gridTemplateColumns={{ md: "repeat(3,1fr)", xs: "repeat(2,1fr)" }}>
                        <CountCard label='Attempt Started At' icon={Icons.default.Clock} value={`${formatDate( participation.participation.exam_participation_start_time )}, ${new Date( participation.participation.exam_participation_start_time ).toLocaleTimeString()}`} />
                        <CountCard label='Attempt No.' icon={Icons.default.ScoreBoard} value={participation.participation.exam_participation_attempt} />
                        <CountCard label='Scores' icon={Icons.default.Money} value={`${participation.participation.exam_participation_score ? participation.participation.exam_participation_score : 0} out of ${participation.marks.sum}`} />
                    </Box>
                    <Box display="flex" marginTop="20px" flexGrow={1} flexDirection="column" gap="10px" borderRadius="5px" className="custom-scrollbar">
                        <Box overflow="auto" display="flex" flexDirection="column" gap="10px" className="custom-scrollbar">
                            {questions && answers &&
                                <Box border={border[1]} sx={{ flexGrow: 1, fontSize: "14px", borderRadius: "5px" }}>
                                    <Box sx={{ cursor: "pointer" }} display="flex" onClick={() => setShowRemarks( !showRemarks )} justifyContent="space-between" bgcolor={palette.contentBg} alignItems="center">
                                        <Typography padding="10px" variant='subtitle2'>Remark</Typography>
                                        <IconButton size="small">
                                            {showRemarks ? Icons.default.KeyboardArrowUp : Icons.default.KeyboardArrowDownIcon}
                                        </IconButton>
                                    </Box>
                                    {showRemarks && <Box padding="20px">{participation.participation.exam_participation_remark ? <Typography variant='body2'>{participation.participation.exam_participation_remark}</Typography> : <Typography variant='body2' color="textSecondary">No remark given!</Typography>}</Box>}
                                </Box>}
                            {questions && answers && questions.map( ( data, index ) => {
                                const answeredCorrectly = !answers[data.question.question_id] || answers[data.question.question_id]?.some( ans => ans.submission_score === 0 )
                                return (
                                    <Box maxWidth="800px" borderRadius="5px" border={border[1]} key={index} padding="10px">
                                        <Box sx={{ fontWeight: "600", "& p": { margin: 0, display: "inline-block" } }} >
                                            <Box display="flex" flexWrap="wrap" alignItems="center" justifyContent="space-between">
                                                <Typography variant='body2' fontSize="10px" component="em" >Question {index + 1}</Typography>
                                            </Box>
                                            <Box fontSize="14px" color={palette.common.font} width="100%" sx={{ wordBreak: "break-word" }} fontWeight={600} textAlign="justify">
                                                {data.question.question_text.trim().includes( 'LATEX-' ) ? <EditableMathField latex={data.question.question_text.trim().split( "LATEX-" )[1]} id='static-latex'> </EditableMathField> : parse( data.question.question_text.trim() )}
                                            </Box>
                                        </Box>
                                        <Box alignItems="center">
                                            <Box border={border[1]} alignItems="center" borderColor={!answeredCorrectly ? palette.success.main : palette.error.main} display="flex" gap="10px" color={!answeredCorrectly ? palette.success.main : palette.error.main} bgcolor={!answeredCorrectly ? palette.success.light + "33" : palette.error.light + "33"} borderRadius="5px" width="fit-content" sx={{ padding: "5px 10px", margin: "10px 0", fontSize: "14px", "& p": { margin: 0 } }}>
                                                {answers[data.question.question_id] && <Typography variant='subtitle2' color='inherit' fontSize="12px">Your answer: </Typography>}
                                                {answers[data.question.question_id] && answers[data.question.question_id].map( ( ans, index ) => (
                                                    ans.answer_id ? <Box key={ans.answer_id} borderRadius="5px" fontSize="14px" sx={{ fontWeight: "bold" }}>{getAnswerText( ans.answer_id, data.options )}</Box> : parse( ans.answer_descriptive )
                                                )
                                                )
                                                }
                                                {!answers[data.question.question_id] && <Typography color='inherit' variant='subtitle2' fontSize="10px">Not answered</Typography>}
                                            </Box>
                                        </Box>
                                        {answers[data.question.question_id] &&
                                            <Box >
                                                {( answers[data.question.question_id].length > 1 || ( answers[data.question.question_id][0].submission_score !== null && answers[data.question.question_id][0].submission_score >= 0 ) )
                                                    ?
                                                    <Typography color='textSecondary' fontSize="12px" >
                                                        Marks: {( answers[data.question.question_id] && answers[data.question.question_id][0].submission_score ) ? answers[data.question.question_id][0].submission_score : 0} / {data.question.question_point}
                                                    </Typography>
                                                    : <Tooltip title="Yet to be evaulated" sx={{ marginLeft: "10px !important" }}>
                                                        <AccessTimeFilledIcon color='error' fontSize='small' />
                                                    </Tooltip>
                                                }
                                            </Box>}
                                    </Box>
                                )
                            } )}

                        </Box>

                    </Box>
                </Box>
            </Box>}

            {isScoreHidden && <Box sx={{ p: 2, bgcolor: palette.error.light + "11", borderRadius: layout.borderRadius, gap: 2, display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Box component="img" src={noDataImg} alt="Hidden scores" width="200px" />
                <Typography variant='subtitle2' sx={{ textAlign: "center" }} >Scores of this exam are hidden! please contact exam conductor for more information.</Typography>

                <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Link to={`/student/exams/${exam_id}`}><Button variant='outlined'>Back to Exam</Button></Link>
                </Box>
            </Box>}
            {inProgress && <Box>
                <DataLoadingSpinner waitingMessage="Loading details..." />
            </Box>}
        </Box >
    )
} )

export default ParticipationDetails